import {
  eventBodyRev,
  isMutDirty,
  systemTimeZone,
  taskToGoogleEvent,
  type RecurrenceOverride,
  type RecurrenceRule,
  type Task,
} from "@app/core";
import { GoogleSyncError, type GoogleEventWrite } from "@app/storage";
import type { ReconcileDeps } from "./types.js";

/**
 * One consistent read of everything a Google event body depends on: the task,
 * its recurrence rule, that rule's overrides, and its undated descendants.
 *
 * Loading it as a unit is what makes the DIRTY test correct. The body a task
 * pushes renders `RRULE` from its rule and `EXDATE` from that rule's cancelled
 * overrides — but the old dirty test looked only at `task.updated_at`, and none
 * of the series operations touch it. So "this and following" (which caps the old
 * rule and spawns a new task) pushed the new series and never shipped the cap:
 * Google kept running the original series forever alongside the new one, which
 * is the duplicate the user sees. Same for cancelling or detaching a single
 * occurrence — the `EXDATE` never left the device.
 */
export interface TaskGraph {
  all: Task[];
  byId: Map<string, Task>;
  ruleById: Map<string, RecurrenceRule>;
  overridesByRule: Map<string, RecurrenceOverride[]>;
  /** subtask_parent_id → UNDATED children. Dated subtasks are detached and get
   *  their own event, so they are excluded (and are dirty in their own right). */
  undatedKidsByParent: Map<string, Task[]>;
}

export async function loadTaskGraph(deps: ReconcileDeps): Promise<TaskGraph> {
  const [all, rules, overrides] = await Promise.all([
    deps.tasks.listAll(),
    deps.recurrence.listAll(),
    deps.recurrenceOverrides.listAll(),
  ]);
  return buildTaskGraph(all, rules, overrides);
}

function buildTaskGraph(
  all: Task[],
  rules: RecurrenceRule[],
  overrides: RecurrenceOverride[],
): TaskGraph {
  const overridesByRule = new Map<string, RecurrenceOverride[]>();
  for (const o of overrides) {
    const list = overridesByRule.get(o.recurrence_id) ?? [];
    list.push(o);
    overridesByRule.set(o.recurrence_id, list);
  }
  const undatedKidsByParent = new Map<string, Task[]>();
  for (const c of all) {
    if (!c.subtask_parent_id || c.due_at) continue;
    const list = undatedKidsByParent.get(c.subtask_parent_id) ?? [];
    list.push(c);
    undatedKidsByParent.set(c.subtask_parent_id, list);
  }
  return {
    all,
    byId: new Map(all.map((t) => [t.id, t] as const)),
    ruleById: new Map(rules.map((r) => [r.id, r] as const)),
    overridesByRule,
    undatedKidsByParent,
  };
}

/** Depth-first collection of all UNDATED descendants. Parent precedes its own
 *  children (pre-order), siblings ordered by `subtask_order`, so the notes block
 *  reflects in-app order. */
function collectAllDescendants(id: string, g: TaskGraph): Task[] {
  const direct = [...(g.undatedKidsByParent.get(id) ?? [])].sort(
    (a, b) => a.subtask_order - b.subtask_order,
  );
  return direct.flatMap((c) => [c, ...collectAllDescendants(c.id, g)]);
}

/** Every row the task's Google event BODY is derived from: the task itself, its
 *  rule, that rule's overrides, and (for a top-level task) its whole undated
 *  subtree. The unit of outbound dirtiness AND of acknowledgement — a push that
 *  delivered the body has delivered all of these rows' content. */
function eventGraphRows(
  g: TaskGraph,
  t: Task,
): { task: Task; rule: RecurrenceRule | undefined; overrides: RecurrenceOverride[]; kids: Task[] } {
  const rule = t.recurrence_id ? g.ruleById.get(t.recurrence_id) : undefined;
  const overrides = t.recurrence_id ? g.overridesByRule.get(t.recurrence_id) ?? [] : [];
  // Child edits ride inside the parent's event, so they dirty the parent. Dated
  // subtasks are their own events and are excluded from this subtree.
  const kids = t.subtask_parent_id ? [] : collectAllDescendants(t.id, g);
  return { task: t, rule, overrides, kids };
}

/**
 * Whether anything the task's Google event body is derived from carries local
 * writes Google has not acknowledged — the outbound dirty signal AND the "was
 * this edited locally since we last shipped it?" signal, read straight off each
 * row (`mut > google_acked_mut`; see core sync/device-local.ts).
 *
 * Spanning the whole body graph — not `task` alone — is load-bearing: the body
 * renders `RRULE` from the rule and `EXDATE` from that rule's cancelled
 * overrides, and no series operation touches the base task at all. When this
 * was inferred from `task.updated_at` vs a cursor, "this and following" pushed
 * the new tail series and never shipped the cap: Google kept running the
 * original series forever alongside the new one.
 */
export function effectiveGoogleDirty(g: TaskGraph, t: Task): boolean {
  const rows = eventGraphRows(g, t);
  if (isMutDirty(rows.task, "google_acked_mut")) return true;
  if (rows.rule && isMutDirty(rows.rule, "google_acked_mut")) return true;
  if (rows.overrides.some((o) => isMutDirty(o, "google_acked_mut"))) return true;
  return rows.kids.some((c) => isMutDirty(c, "google_acked_mut"));
}

/**
 * Acknowledge a handled task: every row of its event graph advances to the
 * `mut` the pass-start graph READ it at — plus, for the task row itself,
 * `ownWrites`: the number of identity-changing link writes the push made while
 * handling it (each is exactly one atomic `mut` bump of our own). The repo
 * clamp (`min(upTo, mut)`, never backwards) keeps any interleaved user edit
 * dirty: its bump pushes `mut` past this expectation, so the row simply goes
 * out again next pass. Also used for "nothing to send" exits (a tombstone or
 * undated row with no remote event) — without an ack those would stay dirty and
 * be re-scanned forever, since no cursor ages them out anymore.
 */
export async function ackEventGraph(
  deps: ReconcileDeps,
  g: TaskGraph,
  t: Task,
  ownWrites: number,
): Promise<void> {
  const rows = eventGraphRows(g, t);
  await deps.tasks.ackMut(t.id, "google", (rows.task.mut ?? 0) + ownWrites);
  if (rows.rule) await deps.recurrence.ackMut(rows.rule.id, "google", rows.rule.mut ?? 0);
  for (const o of rows.overrides) {
    await deps.recurrenceOverrides.ackMut(o.id, "google", o.mut ?? 0);
  }
  for (const c of rows.kids) await deps.tasks.ackMut(c.id, "google", c.mut ?? 0);
}

/** Build the exact body we would write to Google for `t`, from `g`. Pure and
 *  deterministic — {@link bodyRevOf} over it is the write nonce. */
export function buildEventBody(
  g: TaskGraph,
  t: Task,
  floatingTimeZone?: string | null,
): GoogleEventWrite {
  const rule = t.recurrence_id ? g.ruleById.get(t.recurrence_id) : undefined;
  const ovs = t.recurrence_id ? g.overridesByRule.get(t.recurrence_id) ?? [] : [];
  const kids = collectAllDescendants(t.id, g).filter((c) => !c.deleted_at);
  const parentTask = t.subtask_parent_id ? g.byId.get(t.subtask_parent_id) : undefined;
  return taskToGoogleEvent(t, rule, ovs, kids, parentTask, {
    floatingTimeZone: floatingTimeZone ?? systemTimeZone(),
  });
}

/** The write nonce for the body a task would produce right now. Matching a remote
 *  event's `tcRev` proves the remote is byte-identical to our own last write. */
function bodyRevOf(g: TaskGraph, t: Task, floatingTimeZone?: string | null): string {
  return eventBodyRev(buildEventBody(g, t, floatingTimeZone));
}

export function isGone(e: unknown): boolean {
  return e instanceof GoogleSyncError && (e.status === 404 || e.status === 410);
}

export function iso(now: Date): string {
  return now.toISOString();
}
